import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Download,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Save,
  Star,
  Upload,
  X,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";

import {
  entities as entitiesApi,
  entityAttachments as attachmentsApi,
  relationships as relApi,
  signals as signalsApi,
  type ApiEntity,
  type ApiEntityAttachment,
  type ApiRelationship,
  type ApiSignal,
} from "@/lib/api";
import { useAuthenticatedImage } from "@/lib/authenticated-image";
import { colorForEntity, SWATCHES } from "@/lib/entity-color";

/**
 * EntitySlideOver — opens from the right whenever `?entity=<id>` is in the
 * route's search params. Closes by clearing that param. The canvases open it
 * via `navigate({ search: { entity: id } })`.
 */
export function EntitySlideOver({
  workspaceId,
  entityId,
}: {
  workspaceId: string;
  entityId: string | undefined;
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const close = () => {
    setEditing(false);
    navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, entity: undefined }),
    });
  };

  // Pull all entities + their relationships + signals once. Slide-over reads
  // from the same cache the canvases use, so opens are instant after first load.
  const ents = useQuery({
    queryKey: ["entities", workspaceId],
    queryFn: () => entitiesApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const rels = useQuery({
    queryKey: ["relationships", workspaceId],
    queryFn: () => relApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });
  const sigs = useQuery({
    queryKey: ["signals", workspaceId],
    queryFn: () => signalsApi.list(workspaceId),
    enabled: Boolean(workspaceId),
  });

  const entity = useMemo<ApiEntity | undefined>(
    () => ents.data?.find((e) => e.id === entityId),
    [ents.data, entityId],
  );
  const entityById = useMemo(
    () => Object.fromEntries((ents.data ?? []).map((e) => [e.id, e])),
    [ents.data],
  );
  const relevantRels = useMemo<ApiRelationship[]>(
    () =>
      (rels.data ?? []).filter(
        (r) => r.source_entity_id === entityId || r.target_entity_id === entityId,
      ),
    [rels.data, entityId],
  );
  const relevantSigs = useMemo<ApiSignal[]>(
    () =>
      (sigs.data ?? [])
        .filter((s) => s.related_entity_ids?.includes(entityId ?? ""))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
    [sigs.data, entityId],
  );

  const open = Boolean(entityId);

  return (
    <>
      {/* Backdrop */}
      <div
        aria-hidden
        onClick={close}
        className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        className={`fixed right-0 top-0 z-50 h-full w-[400px] max-w-[90vw] border-l border-border bg-background shadow-2xl transition-transform ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {open && (
          <div className="flex h-full flex-col">
            <header className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="label-eyebrow">Entity</div>
              <div className="flex items-center gap-1">
                {entity && !editing && (
                  <button
                    onClick={() => setEditing(true)}
                    className="rounded p-1 text-muted-foreground hover:bg-surface-elevated hover:text-foreground"
                    aria-label="Edit entity"
                    title="Edit entity"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  onClick={close}
                  className="rounded p-1 text-muted-foreground hover:bg-surface-elevated hover:text-foreground"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </header>
            {!entity ? (
              <div className="flex-1 grid place-items-center text-[12px] text-muted-foreground">
                Entity not found
              </div>
            ) : editing ? (
              <EntityEditForm
                workspaceId={workspaceId}
                entity={entity}
                onCancel={() => setEditing(false)}
                onSaved={() => setEditing(false)}
              />
            ) : (
              <div className="flex-1 min-h-0 overflow-auto p-5 space-y-5">
                <EntityHeader workspaceId={workspaceId} entity={entity} />

                <Section title="Media">
                  <MediaSection workspaceId={workspaceId} entityId={entity.id} />
                </Section>

                <Section title="Color">
                  <ColorPicker workspaceId={workspaceId} entity={entity} />
                </Section>

                {Object.entries(entity.attributes ?? {}).length > 0 && (
                  <Section title="Attributes">
                    <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-y-1.5 text-[12px]">
                      {Object.entries(entity.attributes).map(([k, v]) => (
                        <Row key={k} label={k} value={String(v)} />
                      ))}
                    </dl>
                  </Section>
                )}

                <Section title={`Relationships (${relevantRels.length})`}>
                  {relevantRels.length === 0 ? (
                    <Empty text="No relationships." />
                  ) : (
                    <ul className="space-y-1.5 text-[12px]">
                      {relevantRels.map((r) => {
                        const isOut = r.source_entity_id === entity.id;
                        const otherId = isOut ? r.target_entity_id : r.source_entity_id;
                        const other = entityById[otherId];
                        return (
                          <li key={r.id} className="flex items-center gap-2">
                            <span className="text-muted-foreground">{isOut ? "→" : "←"}</span>
                            <span className="font-mono text-[11px] text-muted-foreground">{r.type}</span>
                            <span className="truncate">{other?.name ?? otherId.slice(0, 8)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Section>

                <Section title={`Signals (${relevantSigs.length})`}>
                  {relevantSigs.length === 0 ? (
                    <Empty text="No signals." />
                  ) : (
                    <ul className="space-y-2 text-[12px]">
                      {relevantSigs.slice(0, 10).map((s) => {
                        const summary =
                          (s.attributes?.title as string) ||
                          (s.attributes?.description as string) ||
                          (s.attributes?.summary as string) ||
                          "";
                        return (
                          <li key={s.id} className="border-l-2 border-border pl-3">
                            <div className="font-mono text-[10px] text-muted-foreground">
                              {new Date(s.timestamp).toLocaleString()} · {s.type}
                            </div>
                            {summary && <div className="mt-0.5">{summary}</div>}
                          </li>
                        );
                      })}
                      {relevantSigs.length > 10 && (
                        <li className="text-[10px] text-muted-foreground">
                          +{relevantSigs.length - 10} more in the timeline
                        </li>
                      )}
                    </ul>
                  )}
                </Section>

                <Section title="Metadata">
                  <dl className="grid grid-cols-[120px_minmax(0,1fr)] gap-y-1.5 text-[12px]">
                    <Row label="ID" value={entity.id.slice(0, 8)} />
                    <Row label="Source" value={entity.source} />
                    <Row label="Confidence" value={entity.confidence.toFixed(2)} />
                    <Row
                      label="Coordinates"
                      value={
                        entity.lat != null && entity.lng != null
                          ? `${entity.lat.toFixed(3)}, ${entity.lng.toFixed(3)}`
                          : "—"
                      }
                    />
                    <Row label="Created" value={new Date(entity.created_at).toLocaleDateString()} />
                  </dl>
                </Section>
              </div>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

function ColorPicker({ workspaceId, entity }: { workspaceId: string; entity: ApiEntity }) {
  const qc = useQueryClient();
  const current = colorForEntity(entity);
  const isCustom = Boolean(entity.color);

  const setColor = useMutation({
    mutationFn: (color: string | null) =>
      entitiesApi.update(workspaceId, entity.id, { color }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entities", workspaceId] });
    },
  });

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[12px]">
        <span
          className="inline-block h-5 w-5 rounded border border-border"
          style={{ background: current }}
          aria-label="current color"
        />
        <span className="font-mono text-foreground/85">{current}</span>
        {isCustom ? (
          <button
            onClick={() => setColor.mutate(null)}
            disabled={setColor.isPending}
            className="ml-auto text-[10px] text-muted-foreground hover:text-foreground underline"
          >
            reset to type default
          </button>
        ) : (
          <span className="ml-auto text-[10px] text-muted-foreground">type default</span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SWATCHES.map((c) => (
          <button
            key={c}
            onClick={() => setColor.mutate(c)}
            disabled={setColor.isPending}
            className="h-6 w-6 rounded border border-border hover:scale-110 transition"
            style={{ background: c, outline: c === current ? "2px solid white" : undefined }}
            aria-label={`set color ${c}`}
            title={c}
          />
        ))}
        <label className="h-6 w-6 rounded border border-dashed border-border grid place-items-center text-muted-foreground hover:text-foreground cursor-pointer text-[10px]" title="Custom hex">
          <input
            type="color"
            className="opacity-0 absolute h-6 w-6"
            value={current}
            onChange={(e) => setColor.mutate(e.target.value)}
          />
          +
        </label>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="label-eyebrow mb-2">{title}</div>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground capitalize">{label.replace(/_/g, " ")}</dt>
      <dd className="text-foreground/95 break-words">{value}</dd>
    </>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="text-[12px] text-muted-foreground italic">{text}</div>;
}

// ---------- Header with primary-image avatar ----------

function EntityHeader({
  workspaceId,
  entity,
}: {
  workspaceId: string;
  entity: ApiEntity;
}) {
  // Pull the attachments list so the header can render the primary image
  // avatar without a separate endpoint. This shares a cache with MediaSection.
  const q = useQuery({
    queryKey: ["entity-attachments", workspaceId, entity.id],
    queryFn: () => attachmentsApi.list(workspaceId, entity.id),
  });
  const primary = q.data?.find((a) => a.is_primary && a.kind === "image");
  return (
    <div className="flex items-center gap-3">
      {primary ? (
        <PrimaryAvatar url={primary.url} name={entity.name} />
      ) : (
        <div
          className="h-12 w-12 shrink-0 rounded-md border border-border bg-surface-elevated grid place-items-center text-[16px] font-semibold text-muted-foreground"
          aria-hidden
        >
          {entity.name.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="min-w-0">
        <div className="text-[20px] font-semibold tracking-tight truncate">
          {entity.name}
        </div>
        <div className="text-[12px] text-muted-foreground uppercase tracking-wider">
          {entity.type}
        </div>
      </div>
    </div>
  );
}

function PrimaryAvatar({ url, name }: { url: string; name: string }) {
  const { src } = useAuthenticatedImage(url);
  return (
    <div className="h-12 w-12 shrink-0 rounded-md border border-border bg-surface-elevated overflow-hidden grid place-items-center">
      {src ? (
        <img src={src} alt={name} className="h-full w-full object-cover" />
      ) : (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}

// ---------- Media section — gallery + PDFs + upload ----------

function MediaSection({
  workspaceId,
  entityId,
}: {
  workspaceId: string;
  entityId: string;
}) {
  const qc = useQueryClient();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const q = useQuery({
    queryKey: ["entity-attachments", workspaceId, entityId],
    queryFn: () => attachmentsApi.list(workspaceId, entityId),
  });

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: ["entity-attachments", workspaceId, entityId],
    });

  const upload = useMutation({
    mutationFn: (file: File) => attachmentsApi.upload(workspaceId, entityId, file),
    onSuccess: () => {
      setUploadError(null);
      invalidate();
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("415"))
        setUploadError("Unsupported file type. Use JPG, PNG, WebP, GIF, or PDF.");
      else if (msg.includes("413"))
        setUploadError("File too large. Max 5 MB.");
      else setUploadError(msg);
    },
  });

  const attachments = q.data ?? [];
  const images = attachments.filter((a) => a.kind === "image");
  const documents = attachments.filter((a) => a.kind === "document");

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5">
          {images.map((a) => (
            <ImageThumb
              key={a.id}
              attachment={a}
              workspaceId={workspaceId}
              entityId={entityId}
              onChange={invalidate}
            />
          ))}
        </div>
      )}

      {documents.length > 0 && (
        <div className="space-y-1">
          {documents.map((a) => (
            <DocumentRow
              key={a.id}
              attachment={a}
              workspaceId={workspaceId}
              entityId={entityId}
              onChange={invalidate}
            />
          ))}
        </div>
      )}

      {attachments.length === 0 && !q.isLoading && (
        <div className="text-[11.5px] text-muted-foreground italic">
          No media yet.
        </div>
      )}

      {/* Upload button */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
            e.target.value = ""; // allow re-uploading same file
          }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={upload.isPending}
          className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-surface hover:bg-surface-elevated py-2 text-[11.5px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          {upload.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          {upload.isPending ? "Uploading…" : "Upload image or PDF"}
        </button>
        {uploadError && (
          <div className="mt-1.5 text-[11px] text-risk-high">{uploadError}</div>
        )}
        <div className="mt-1 text-[10px] text-muted-foreground">
          Max 5 MB per file. Supported: JPG, PNG, WebP, GIF, PDF.
        </div>
      </div>
    </div>
  );
}

function ImageThumb({
  attachment,
  workspaceId,
  entityId,
  onChange,
}: {
  attachment: ApiEntityAttachment;
  workspaceId: string;
  entityId: string;
  onChange: () => void;
}) {
  const { src } = useAuthenticatedImage(attachment.url);
  const [hovered, setHovered] = useState(false);
  const setPrimary = useMutation({
    mutationFn: () =>
      attachmentsApi.setPrimary(workspaceId, entityId, attachment.id),
    onSuccess: onChange,
  });
  const remove = useMutation({
    mutationFn: () => attachmentsApi.remove(workspaceId, entityId, attachment.id),
    onSuccess: onChange,
  });

  return (
    <div
      className="relative aspect-square rounded-md overflow-hidden border border-border bg-surface-elevated group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {src ? (
        <img
          src={src}
          alt={attachment.filename}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="h-full w-full grid place-items-center">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        </div>
      )}
      {attachment.is_primary && (
        <div className="absolute top-1 left-1 rounded-sm bg-primary/90 px-1 py-0.5 text-[9px] font-semibold text-primary-foreground flex items-center gap-0.5">
          <Star className="h-2.5 w-2.5 fill-current" />
          Primary
        </div>
      )}
      {(hovered || setPrimary.isPending || remove.isPending) && (
        <div className="absolute inset-0 bg-black/60 grid place-items-center gap-1">
          {!attachment.is_primary && (
            <button
              onClick={() => setPrimary.mutate()}
              disabled={setPrimary.isPending}
              className="rounded bg-primary/80 hover:bg-primary text-primary-foreground px-2 py-0.5 text-[10px] font-semibold"
              title="Set as primary image"
            >
              Set primary
            </button>
          )}
          <button
            onClick={() => {
              if (confirm(`Delete ${attachment.filename}?`)) remove.mutate();
            }}
            disabled={remove.isPending}
            className="rounded bg-risk-high/80 hover:bg-risk-high text-white px-2 py-0.5 text-[10px] font-semibold"
            title="Delete"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- Edit form ----------

/**
 * Edit mode for the entity's metadata. Handles name, type, region, lat/lng
 * and attributes (add / edit / delete). Attribute deletion works via the
 * backend's "null-value = delete this key" semantic in services.entities
 * update_entity — see that docstring for the contract.
 *
 * Color, media, relationships and signals keep their own in-place editors
 * and are not part of this form. This form owns only the fields for which
 * a batch save makes sense.
 */
function EntityEditForm({
  workspaceId,
  entity,
  onCancel,
  onSaved,
}: {
  workspaceId: string;
  entity: ApiEntity;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const qc = useQueryClient();

  const [name, setName] = useState(entity.name);
  const [type, setType] = useState(entity.type);
  const [region, setRegion] = useState(entity.region ?? "");
  const [latStr, setLatStr] = useState(
    entity.lat != null ? String(entity.lat) : "",
  );
  const [lngStr, setLngStr] = useState(
    entity.lng != null ? String(entity.lng) : "",
  );
  // Represent attributes as an ordered array of {key, value} rows so the
  // operator can edit and delete rows without losing insertion order or
  // stepping on duplicate keys during typing. We collapse back to a dict
  // (with dedup) at save time.
  const initialAttrs = useMemo(
    () =>
      Object.entries(entity.attributes ?? {}).map(([k, v]) => ({
        key: k,
        value: v == null ? "" : String(v),
      })),
    [entity.attributes],
  );
  const [attrRows, setAttrRows] = useState(initialAttrs);
  const [error, setError] = useState<string | null>(null);

  const nameTrim = name.trim();
  const typeTrim = type.trim();

  const update = useMutation({
    mutationFn: async () => {
      // Build the patch. Only include fields that actually changed — we
      // don't want to overwrite something we didn't touch (e.g. color set
      // through the swatch picker while this form was open).
      const patch: Record<string, unknown> = {};
      if (nameTrim !== entity.name) patch.name = nameTrim;
      if (typeTrim !== entity.type) patch.type = typeTrim;

      // Geolocation is a nested field on the domain model. If any of
      // region/lat/lng changed, send the whole triple (partial values
      // still work; backend just applies whichever are present).
      const regionTrim = region.trim();
      const latParsed = latStr.trim() === "" ? null : Number(latStr);
      const lngParsed = lngStr.trim() === "" ? null : Number(lngStr);
      const regionChanged = regionTrim !== (entity.region ?? "");
      const latChanged =
        (entity.lat ?? null) !== (latParsed == null ? null : latParsed);
      const lngChanged =
        (entity.lng ?? null) !== (lngParsed == null ? null : lngParsed);
      if (regionChanged || latChanged || lngChanged) {
        patch.geolocation = {
          lat: latParsed,
          lng: lngParsed,
          region: regionTrim || null,
        };
      }

      // Attributes: build the new dict from rows, then add null-sentinels
      // for keys the user removed. Skip rows with empty keys.
      const newAttrs: Record<string, unknown> = {};
      for (const row of attrRows) {
        const k = row.key.trim();
        if (!k) continue;
        newAttrs[k] = row.value;
      }
      const originalKeys = new Set(Object.keys(entity.attributes ?? {}));
      const currentKeys = new Set(Object.keys(newAttrs));
      const deletedKeys = [...originalKeys].filter((k) => !currentKeys.has(k));
      // Detect actual attribute changes: added, removed, or value-modified
      const originalAttrs = entity.attributes ?? {};
      const attrsChanged =
        deletedKeys.length > 0 ||
        Object.keys(newAttrs).some(
          (k) => String(originalAttrs[k] ?? "") !== String(newAttrs[k] ?? ""),
        );
      if (attrsChanged) {
        const attributesPatch: Record<string, unknown> = { ...newAttrs };
        for (const k of deletedKeys) attributesPatch[k] = null;
        patch.attributes = attributesPatch;
      }

      if (Object.keys(patch).length === 0) {
        // Nothing to save. Return the current entity so the mutation reports
        // success and the UI closes edit mode without an unnecessary POST.
        return entity;
      }
      return await entitiesApi.update(workspaceId, entity.id, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["entities", workspaceId] });
      onSaved();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const canSave =
    nameTrim.length > 0 &&
    typeTrim.length > 0 &&
    (latStr.trim() === "" || !Number.isNaN(Number(latStr))) &&
    (lngStr.trim() === "" || !Number.isNaN(Number(lngStr))) &&
    // No duplicate keys among rows with non-empty keys
    (() => {
      const seen = new Set<string>();
      for (const r of attrRows) {
        const k = r.key.trim();
        if (!k) continue;
        if (seen.has(k)) return false;
        seen.add(k);
      }
      return true;
    })();

  return (
    <div className="flex-1 min-h-0 overflow-auto">
      {/* Sticky action bar — always visible while editing so operator never
          scrolls to find Save/Cancel. */}
      <div className="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-border bg-background/95 backdrop-blur px-4 py-2">
        <div className="text-[11px] text-muted-foreground">
          Editing entity
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            disabled={update.isPending}
            className="text-[11.5px] text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() => update.mutate()}
            disabled={!canSave || update.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90"
          >
            {update.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {error && (
          <div className="rounded-md border border-risk-high/40 bg-risk-high/10 px-3 py-2 text-[12px] text-risk-high">
            {error}
          </div>
        )}

        <Section title="Identity">
          <div className="space-y-2.5">
            <LabeledInput
              label="Name"
              value={name}
              onChange={setName}
              placeholder="Entity name"
              required
              autoFocus
            />
            <LabeledInput
              label="Type"
              value={type}
              onChange={setType}
              placeholder="e.g. armed_group, facility, person"
              required
              hint="Should match a type in this workspace's ontology."
            />
          </div>
        </Section>

        <Section title="Location">
          <div className="space-y-2.5">
            <LabeledInput
              label="Region"
              value={region}
              onChange={setRegion}
              placeholder="e.g. Colombia, Cauca, Bogotá metro"
            />
            <div className="grid grid-cols-2 gap-2">
              <LabeledInput
                label="Latitude"
                value={latStr}
                onChange={setLatStr}
                placeholder="e.g. 4.71"
                inputMode="decimal"
              />
              <LabeledInput
                label="Longitude"
                value={lngStr}
                onChange={setLngStr}
                placeholder="e.g. -74.07"
                inputMode="decimal"
              />
            </div>
            {(latStr.trim() !== "" && Number.isNaN(Number(latStr))) ||
            (lngStr.trim() !== "" && Number.isNaN(Number(lngStr))) ? (
              <div className="text-[11px] text-risk-high">
                Lat / Lng must be numbers, or left empty.
              </div>
            ) : null}
          </div>
        </Section>

        <Section title="Attributes">
          <AttributesEditor rows={attrRows} onChange={setAttrRows} />
        </Section>

        <p className="text-[11px] text-muted-foreground">
          Color, media, relationships and signals are managed in their own
          panels — they save changes immediately and are not part of this
          form.
        </p>
      </div>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  required,
  hint,
  autoFocus,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  hint?: string;
  autoFocus?: boolean;
  inputMode?: "text" | "decimal" | "numeric";
}) {
  return (
    <label className="block">
      <div className="text-[10.5px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">
        {label}
        {required && <span className="text-risk-high"> *</span>}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputMode={inputMode}
        className="w-full rounded-md border border-border bg-surface px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
      />
      {hint && (
        <div className="mt-0.5 text-[10.5px] text-muted-foreground">{hint}</div>
      )}
    </label>
  );
}

function AttributesEditor({
  rows,
  onChange,
}: {
  rows: { key: string; value: string }[];
  onChange: (rows: { key: string; value: string }[]) => void;
}) {
  // Track duplicate keys client-side so the operator sees the warning inline.
  const keyCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const k = r.key.trim();
      if (!k) continue;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [rows]);

  const updateRow = (
    i: number,
    partial: Partial<{ key: string; value: string }>,
  ) => {
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...partial } : r)));
  };
  const remove = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { key: "", value: "" }]);

  return (
    <div className="space-y-1.5">
      {rows.length === 0 ? (
        <div className="text-[11.5px] text-muted-foreground italic">
          No attributes yet.
        </div>
      ) : (
        rows.map((r, i) => {
          const trimmed = r.key.trim();
          const dup = trimmed !== "" && (keyCounts.get(trimmed) ?? 0) > 1;
          return (
            <div key={i} className="flex items-start gap-1.5">
              <div className="flex-1 min-w-0">
                <input
                  value={r.key}
                  onChange={(e) => updateRow(i, { key: e.target.value })}
                  placeholder="key"
                  className={`w-full rounded-md border bg-surface px-2 py-1 text-[12px] font-mono focus:outline-none focus:ring-1 focus:ring-primary ${
                    dup
                      ? "border-risk-high/60"
                      : "border-border"
                  }`}
                />
                {dup && (
                  <div className="mt-0.5 text-[10.5px] text-risk-high">
                    Duplicate key
                  </div>
                )}
              </div>
              <input
                value={r.value}
                onChange={(e) => updateRow(i, { value: e.target.value })}
                placeholder="value"
                className="flex-[1.4] rounded-md border border-border bg-surface px-2 py-1 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                onClick={() => remove(i)}
                className="mt-0.5 rounded p-1 text-muted-foreground hover:bg-risk-high/10 hover:text-risk-high"
                aria-label="Remove attribute"
                title="Remove attribute"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })
      )}
      <button
        onClick={add}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-surface px-2.5 py-1 text-[11.5px] font-semibold text-muted-foreground hover:bg-surface-elevated hover:text-foreground"
      >
        <Plus className="h-3 w-3" /> Add attribute
      </button>
    </div>
  );
}

function DocumentRow({
  attachment,
  workspaceId,
  entityId,
  onChange,
}: {
  attachment: ApiEntityAttachment;
  workspaceId: string;
  entityId: string;
  onChange: () => void;
}) {
  const remove = useMutation({
    mutationFn: () => attachmentsApi.remove(workspaceId, entityId, attachment.id),
    onSuccess: onChange,
  });
  const download = async () => {
    // Use authenticated fetch to get the file, then trigger a download.
    const { getAccessToken } = await import("@/lib/auth");
    const bearer = getAccessToken();
    const res = await fetch(attachment.url, {
      credentials: "include",
      headers: bearer ? { authorization: `Bearer ${bearer}` } : undefined,
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = attachment.filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-surface p-2 text-[12px]">
      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">{attachment.filename}</div>
        <div className="text-[10px] text-muted-foreground">
          {(attachment.size_bytes / 1024).toFixed(0)} KB
        </div>
      </div>
      <button
        onClick={download}
        className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-surface-elevated"
        title="Download"
      >
        <Download className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => {
          if (confirm(`Delete ${attachment.filename}?`)) remove.mutate();
        }}
        disabled={remove.isPending}
        className="text-muted-foreground hover:text-risk-high p-1 rounded hover:bg-surface-elevated"
        title="Delete"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
